// src/components/PaneMain.jsx
import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import DrawingIntake from './DrawingIntake.jsx';
import { getElevationAtPoint } from '../utils/elevation.js';
import { lonLatToLocalXZ } from '../utils/coordinateConversion';
import SheetPanel from './SheetPanel.jsx';
import DesignAnalysisPanel from './DesignAnalysisPanel.jsx';
import AnalysisAssistant from './AnalysisAssistant.jsx';
import { API_BASE } from '../utils/apiBase';

// --- NEW: helpers to compute origin edge + bearing ---

// Simple bearing between two lat/lng points, returns 0–360° (north-based)
function computeBearingDeg(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  const θ = Math.atan2(y, x);
  const brng = (θ * 180) / Math.PI;
  return (brng + 360) % 360;
}

// Choose the bottom edge (smallest lat) and its left-most point (smallest lng)
// Returns the bearing of that edge and the index of the origin vertex.
function computeOriginEdgeBearingFromPolygon(latlngs) {
  if (!latlngs || latlngs.length < 2) {
    return { bearingDeg: null, originIndex: 0 };
  }

  // Find lowest (southernmost) vertex, break ties by smallest lng (left-most)
  let originIndex = 0;
  for (let i = 1; i < latlngs.length; i++) {
    const p = latlngs[i];
    const o = latlngs[originIndex];
    if (p.lat < o.lat || (p.lat === o.lat && p.lng < o.lng)) {
      originIndex = i;
    }
  }

  const origin = latlngs[originIndex];
  const next = latlngs[(originIndex + 1) % latlngs.length];

  const bearingDeg = computeBearingDeg(
    origin.lat,
    origin.lng,
    next.lat,
    next.lng
  );

  return { bearingDeg, originIndex };
}


/**
 * A project record from a site polygon, with the same fields the map's Save
 * writes (see handleConfirmSave) — so a project started from a drawing is, to
 * everything downstream, indistinguishable from one traced on the map.
 */
function buildSiteProject({ name, polygonLatLng, areaM2, perimeterM, location }) {
  const n = polygonLatLng.length;
  const centerLat = polygonLatLng.reduce((acc, p) => acc + p.lat, 0) / n;
  const centerLng = polygonLatLng.reduce((acc, p) => acc + p.lng, 0) / n;
  const localPoints = polygonLatLng.map((pt) => {
    const p = lonLatToLocalXZ(pt.lng, pt.lat, centerLng, centerLat);
    return { x: p.x, z: -p.z };
  });
  const originLocalX = localPoints.reduce((acc, p) => acc + p.x, 0) / n;
  const originLocalZ = localPoints.reduce((acc, p) => acc + p.z, 0) / n;
  const { bearingDeg: originEdgeBearingDeg, originIndex } = computeOriginEdgeBearingFromPolygon(polygonLatLng);
  return {
    name,
    areaM2,
    perimeterM,
    areaSqft: areaM2 * 10.764,
    areaAcre: areaM2 * 0.000247,
    polygonLatLng,
    createdAt: new Date().toISOString(),
    originEdgeBearingDeg,
    originIndex,
    originLocalX,
    originLocalZ,
    centerLat,
    centerLng,
    location: location || '',
  };
}

/* Split out of the first download.
 *
 * These two views carry the heavy libraries — Site3DView pulls in three.js
 * (348K + 372K minified) and MapWithDrawing pulls in Leaflet (148K) — and the
 * landing screen uses neither. Imported statically they were fetched, parsed
 * and executed before anyone had chosen how to start a project, which on a slow
 * connection is most of the wait.
 *
 * Both already render behind a mode check, so the split points cost nothing:
 * three.js arrives when a project is first opened, Leaflet when the map is
 * first asked for. Contours is not split separately — it lives inside
 * Site3DView and already imports three, so it travels in that chunk anyway.
 */
const MapWithDrawing = lazy(() => import('./MapWithDrawing.jsx'));
const Site3DView = lazy(() => import('./Site3DView.jsx'));

/** Shown while a view's chunk is in flight. Names what is loading, because a
 *  bare spinner during a 700 KB download reads as the app having hung. */
function ViewLoading({ what }) {
  return (
    <div className="view-loading" role="status" aria-live="polite">
      <span className="view-loading-spinner" aria-hidden="true" />
      <span>Loading {what}…</span>
    </div>
  );
}

/* Line icons rather than artwork: they inherit the theme's accent, stay sharp
   at any size, weigh nothing, and carry no stock-library licence. */
const IconMap = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4Z" />
    <path d="M9 4v13M15 6.5v13" />
  </svg>
);
const IconSketch = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20h16" />
    <path d="M6 16.5 15.2 7.3a2 2 0 0 1 2.8 2.8L8.8 19.3 5 20l.7-3.8Z" />
    <path d="m13.5 9 2.8 2.8" />
  </svg>
);
const IconPhoto = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="8.5" cy="10" r="1.6" />
    <path d="m3.5 17 4.8-4.4a1.6 1.6 0 0 1 2.2 0L15 17" />
    <path d="m13.5 14 2.1-2a1.6 1.6 0 0 1 2.2 0l2.7 2.5" />
  </svg>
);
const IconPdf = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 16.5h4" />
  </svg>
);

/** The ways into a project, in the order most people take them. */
const ACTIONS = [
  { mode: 'map', label: 'Locate your Site', primary: true, icon: IconMap, art: 'map',
    desc: 'Find the plot on a map and trace its boundary. Brings in roads, context and levels.' },
  { mode: 'sketch', label: 'Sketch your Site', icon: IconSketch, art: 'sketch',
    desc: 'Draw the site or plan by hand — mouse, pen or touch — and start a project from it.' },
  { mode: 'photo', label: 'Upload a Photo', icon: IconPhoto, art: 'photo',
    desc: 'Choose or take a photo of a site or floor plan, and start a project from it.' },
  // NOTE: pdf_upload.jpg was byte-identical to image_upload.jpg — the same
  // picture on two cards. Until a real asset exists this one is mirrored so the
  // pair is at least not literally the same image.
  { mode: 'pdf', label: 'Upload a PDF', icon: IconPdf, art: 'pdf',
    desc: 'A site or floor plan as a PDF, and start a project from it.' },
];

function PaneMain({
  savedProjects,
  setSavedProjects,
  selectedProject,
  mainMode,
  setMainMode,
  imageAnalysisResult,
}) {
  const [mode, setMode] = useState('buttons'); // legacy local mode
  const [siteArea, setSiteArea] = useState(0); // m² (from Leaflet)
  const [sitePerimeter, setSitePerimeter] = useState(0); // m (from Leaflet)
  const [locationLabel, setLocationLabel] = useState('');
  // polygon from MapWithDrawing (Leaflet LatLng objects)
  const [polygonLatLngs, setPolygonLatLngs] = useState([]);

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('');

  const hasMeasurement = siteArea > 0 && sitePerimeter > 0;

  const handleLocationChange = (label) => {
    setLocationLabel(label || '');
  };

  // called from MapWithDrawing when polygon created/edited
  const handlePolygonPointsChange = (latlngs) => {
    setPolygonLatLngs(latlngs || []);
  };

  // compute approximate center of polygon in lat/lng
  const computePolygonCenter = () => {
    if (!polygonLatLngs.length) return { lat: null, lng: null };
    let sumLat = 0;
    let sumLng = 0;
    polygonLatLngs.forEach((p) => {
      sumLat += p.lat;
      sumLng += p.lng;
    });
    return {
      lat: sumLat / polygonLatLngs.length,
      lng: sumLng / polygonLatLngs.length,
    };
  };

  const handleSaveProject = () => {
    if (!hasMeasurement) return;
    setIsSaveModalOpen(true);
  };

  const handleConfirmSave = async () => {
    console.log('handleConfirmSave called');
    const areaSqft = siteArea * 10.764;
    const areaAcre = siteArea * 0.000247;

    const { lat: centerLat, lng: centerLng } = computePolygonCenter();
    if (!centerLat || !centerLng) {
      console.error('No polygon center, cannot save project');
      return;
    }

    // Store the raw lat/lng polygon; Site3DView will generate metric local coords.
    const localPoints = polygonLatLngs.map((pt) => {
      const p = lonLatToLocalXZ(pt.lng, pt.lat, centerLng, centerLat);
      return { x: p.x, z: -p.z }; // keep same convention as 3D
    });

    let sumX = 0;
    let sumZ = 0;
    localPoints.forEach((p) => {
      sumX += p.x;
      sumZ += p.z;
    });
    const originLocalX = sumX / localPoints.length;
    const originLocalZ = sumZ / localPoints.length;

    // NEW: compute origin index (left point of bottom edge) + north-bearing of that edge
    const {
      bearingDeg: originEdgeBearingDeg,
      originIndex,
    } = computeOriginEdgeBearingFromPolygon(polygonLatLngs);

    const location = locationLabel; // may be '' if nothing searched

    const project = {
      name: projectName,
      areaM2: siteArea, // already in m² from Leaflet
      perimeterM: sitePerimeter, // already in m from Leaflet
      areaSqft,
      areaAcre,
      // geometry: original geographic polygon
      polygonLatLng: polygonLatLngs, // canonical geometry
      createdAt: new Date().toISOString(),

      // NEW: store north/origin info for 3D alignment
      originEdgeBearingDeg, // 0–360° compass bearing of bottom edge
      originIndex, // index of left point of bottom edge in polygonLatLng

      originLocalX,
      originLocalZ,
      centerLat,
      centerLng,
      location,
    };

    try {
      const response = await fetch(
        `${API_BASE}/api/projects`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(project),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save project on server');
      }

      const result = await response.json(); // { ok: true, id: number }

      const projectWithId = { ...project, id: result.id };
      setSavedProjects((prev) => [...prev, projectWithId]);
      // Select it in the left pane with View prompting — the same hand-off a
      // project started from a drawing gets.
      window.dispatchEvent(new CustomEvent('openProjectRequested', {
        detail: { projectId: projectWithId.id, project: projectWithId, view: false },
      }));

      setIsSaveModalOpen(false);
      setProjectName('');
    } catch (err) {
      console.error('Failed to save project on server', err);
    }

    try {
      const elevation = await getElevationAtPoint(centerLat, centerLng);
      console.log('Single-point elevation for project:', elevation);
    } catch (error) {
      console.error('Elevation fetch failed:', error);
    }
  };

  const handleCancelSave = () => {
    setIsSaveModalOpen(false);
  };

  const effectiveMode = mainMode || mode;

  const splitRef = useRef(null);
  const [bottomPanel, setBottomPanel] = useState('none'); // 'none' | 'sheet' | 'analysis'
  const [sheetPct, setSheetPct] = useState(50);           // bottom pane % height
  const bottomOpen = bottomPanel !== 'none';

  // A project started from a drawing, saved exactly as the map saves one.
  const createDrawingProject = async ({ extra, ...site }) => {
    const project = { ...buildSiteProject(site), ...extra };
    const res = await fetch(`${API_BASE}/api/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    });
    const raw = await res.text();
    let d = null;
    try { d = JSON.parse(raw); } catch { d = null; }
    if (res.status === 401) throw new Error('Your session has ended. Sign in again, then save.');
    if (!res.ok || !d?.ok) throw new Error(d?.error || d?.message || `The project could not be saved (HTTP ${res.status}).`);
    const saved = { ...project, id: d.id };
    setSavedProjects((prev) => [...(prev || []), saved]);
    return saved;
  };

  // After saving: back to the start screen with the new project selected in the
  // tree, as the map flow leaves it. The owner opens it with View.
  const handleDrawingProjectSaved = (saved) => {
    setMode('buttons');
    setMainMode?.('buttons');
    window.dispatchEvent(new CustomEvent('openProjectRequested', { detail: { projectId: saved.id, project: saved, view: false } }));
  };

  // echo drawer state so the buttons that open it (PaneLeft/PaneRight) can show
  // whether they are currently active — the drawer can also close on its own.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('bottomPanelChanged', { detail: { panel: bottomPanel } }));
  }, [bottomPanel]);

  // Sheet (PaneLeft) and Design Analysis (PaneRight) share one drawer, mutually exclusive
  useEffect(() => {
    const onSheet = () => setBottomPanel((p) => (p === 'sheet' ? 'none' : 'sheet'));
    const onAnalysis = () => setBottomPanel((p) => (p === 'analysis' ? 'none' : 'analysis'));
    const onCloseAnalysis = () => setBottomPanel((p) => (p === 'analysis' ? 'none' : p));
    window.addEventListener('toggleSheetRequested', onSheet);
    window.addEventListener('toggleAnalysisRequested', onAnalysis);
    window.addEventListener('closeAnalysisRequested', onCloseAnalysis);
    return () => {
      window.removeEventListener('toggleSheetRequested', onSheet);
      window.removeEventListener('toggleAnalysisRequested', onAnalysis);
      window.removeEventListener('closeAnalysisRequested', onCloseAnalysis);
    };
  }, []);

  const startDrag = (e) => {
    e.preventDefault();
    const onMove = (ev) => {
      const rect = splitRef.current?.getBoundingClientRect();
      if (!rect) return;
      const fromBottom = rect.bottom - ev.clientY;
      const pct = (fromBottom / rect.height) * 100;
      setSheetPct(Math.min(80, Math.max(20, pct)));   // clamp 20–80%
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <main className="pane-main">
      <div className="viewport">
        <div className="canvas-placeholder" id="canvas">
          {effectiveMode === 'map' && (
            <Suspense fallback={<ViewLoading what="the map" />}>
              <MapWithDrawing
                onAreaChange={(a) => setSiteArea(a)}
                onPerimeterChange={(p) => setSitePerimeter(p)}
                onPolygonPointsChange={handlePolygonPointsChange}
                onLocationChange={handleLocationChange}
              />

              <div className="site-summary-panel">
                <div>
                  Measured area: {siteArea ? siteArea.toFixed(1) : '—'} m²
                </div>
                <div>
                  Measured perimeter: {sitePerimeter ? sitePerimeter.toFixed(1) : '—'} m
                </div>

                <div className="site-summary-row">
                  <span>Area (sqft):</span>
                  <span>
                    {siteArea ? (siteArea * 10.764).toFixed(1) : '—'}
                  </span>
                </div>

                <div className="site-summary-row">
                  <span>Area (acre):</span>
                  <span>
                    {siteArea ? (siteArea * 0.000247).toFixed(4) : '—'}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className={`save-project-btn ${
                  hasMeasurement ? '' : 'save-project-btn--disabled'
                }`}
                onClick={handleSaveProject}
                disabled={!hasMeasurement}
              >
                Save Project
              </button>

              {isSaveModalOpen && (
                <div className="modal-backdrop">
                  <div className="modal-card">
                    <div className="modal-title">Save Project</div>
                    <label className="modal-field">
                      <span>Project name</span>
                      <input
                        type="text"
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="Enter project name"
                      />
                    </label>
                    <div className="modal-actions">
                      <button
                        type="button"
                        className="btn"
                        onClick={handleCancelSave}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn primary"
                        onClick={() => {
                          console.log(
                            'Save button clicked, projectName =',
                            projectName
                          );
                          handleConfirmSave();
                        }}
                        disabled={!projectName.trim()}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </Suspense>
          )}

          {effectiveMode === '3d' && selectedProject && (
            <div className="stage-with-assistant">
            <div className="split-vertical" ref={splitRef}>
              <div className="split-top" style={{ height: bottomOpen ? `${100 - sheetPct}%` : '100%' }}>
                {imageAnalysisResult && <div className="image-analysis-banner" />}
                <Suspense fallback={<ViewLoading what="the 3D view" />}>
                  <Site3DView
                    project={selectedProject}
                    imageAnalysisDraft={imageAnalysisResult}
                  />
                </Suspense>
                {/* Floats OVER the 3D view rather than taking a band below it,
                    so the view keeps the whole stage. Inside .split-top, not
                    beside it, which is what keeps it clear of the analysis
                    drawer: when that opens, the strip rides up with the top
                    pane instead of covering the drawer's last rows.
                    A question like "what setback applies here?" is one somebody
                    has BEFORE they have filled in the Build tab, so it must not
                    be reachable only by opening a panel about analysis that has
                    not happened yet. Collapsed by default so it costs one line. */}
                <AnalysisAssistant
                  project={selectedProject}
                  apiBase={API_BASE}
                  collapsedDefault
                />
              </div>

              {bottomOpen && (
                <>
                  <div
                    className="split-handle"
                    onMouseDown={startDrag}
                    title="Drag to resize"
                  />
                  <div className="split-bottom" style={{ height: `${sheetPct}%` }}>
                    {bottomPanel === 'sheet' && (
                      <SheetPanel project={selectedProject} setSavedProjects={setSavedProjects} />
                    )}
                    {bottomPanel === 'analysis' && (
                      <DesignAnalysisPanel project={selectedProject} />
                    )}
                  </div>
                </>
              )}
            </div>
            </div>
          )}

          {(effectiveMode === 'sketch' || effectiveMode === 'photo' || effectiveMode === 'pdf') && (
            <DrawingIntake
              key={effectiveMode}
              mode={effectiveMode}
              apiBase={API_BASE}
              onCancel={() => { setMode('buttons'); setMainMode?.('buttons'); }}
              onCreateProject={createDrawingProject}
              onSaved={handleDrawingProjectSaved}
            />
          )}

          {effectiveMode === 'buttons' && (
            <div className="main-actions">
              {/* Four ways in, but not four equal ways: locating the plot on a
                  map is what most projects start with, so it leads and the other
                  three sit beneath it as alternatives. The old grid gave all
                  four the same weight and offered no answer to "where do I
                  start?".

                  The stock illustrations are gone. Two of them were the SAME
                  image with different labels, they were near-white blocks on a
                  dark app — the single brightest thing on screen, against
                  everything the theme is tuned for — and they carried a stock
                  library watermark. These icons are drawn in the accent colour
                  and inherit the theme. */}
              {ACTIONS.map((a) => (
                <button
                  key={a.mode}
                  className={`main-action-btn ${a.primary ? 'main-action-btn--primary' : ''}`}
                  onClick={() => { setMode(a.mode); setMainMode?.(a.mode); }}
                >
                  {/* The artwork sits BEHIND the text, dimmed and desaturated,
                      with a scrim over it. Full-brightness illustration was the
                      loudest thing on a dark screen and the label had to fight
                      it; as a backdrop it fills the card without competing. */}
                  <span className={`main-action-art main-action-art--${a.art}`} aria-hidden="true" />
                  <span className="main-action-icon" aria-hidden="true">{a.icon}</span>
                  <span className="main-action-text">
                    {a.primary && <span className="main-action-eyebrow">Most projects start here</span>}
                    <span className="main-action-label">{a.label}</span>
                    <span className="main-action-desc">{a.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

        </div>
      </div>
    </main>
  );
}

export default PaneMain;
